import React, { useState, useRef } from 'react';
import {
  withStyles, FormControl, FormLabel, FormGroup, Typography,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import {
  Create as CreateIcon,
  DeleteOutline,
  ArrowBackOutlined,
  ArrowForwardOutlined,
  CheckCircleOutlineOutlined,
  WarningOutlined,
} from '@material-ui/icons';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import styles from '../styles';
import PF2MGenericButton from '~/components/PF2MGenericButton';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import {
  Card, CardBody, CardHeader, CardIcon,
} from '~/components/Card';
import { updateEquipmentStatus } from '~/store/dispatch/actions';
import { Field, Form, Formik } from 'formik';
import PF2MDatePickerOutlined from '~/components/PF2MDatePickerOutlined';
import moment from 'moment';
import {
  newMomentDate,
  parseDateTimeToTimestamp,
  verifyTime,
  parseDate,
  subtractDaysFromNow,
} from '~/utils/moment';
import TimeCell from '~/components/PF2MDataTable/Cells/TimeCell';
import usePlatformConfiguration, {
  usePlatformConfigurationUserAccessLevel,
} from '~/services/platformConfiguration';

const FormControlDefaults = ({
  formLabel,
  children,
  classes,
  isRequired,
}) => (
  <FormControl className={classes.modelParent} required={isRequired}>
    <FormLabel className={classes.formLabel}>
      {formLabel}
    </FormLabel>
    <FormGroup>
      {children}
    </FormGroup>
  </FormControl>
);

FormControlDefaults.propTypes = {
  classes: PropTypes.object.isRequired,
  formLabel: PropTypes.string.isRequired,
  children: PropTypes.object.isRequired,
  isRequired: PropTypes.bool,
};

FormControlDefaults.defaultProps = {
  isRequired: false,
};

const EquipmentStatusEditAllModal = ({
  classes, selectedItems, handleClose,
}) => {
  // os campos iniciais precisam ser carregados junto com o componente.
  const INIT_SELECTED_FIELDS = {
    id_code_group: 0,
    code_type: -1,
    id_code: 0,
    start_date: newMomentDate(),
    start_time: moment(new Date()).format('HH:mm:ss'),
  };

  const [isModalOpen, setModalOpen] = useState(false);
  const [formikErrors, setFormikErrors] = useState({});
  const [selectedFields, setSelectedFields] = useState(INIT_SELECTED_FIELDS);
  const [isConfirmChangesStep, setIsConfirmChangesStep] = useState(false);
  const formikRef = useRef();
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();

  const codes = useSelector(state => state.nref.codes);
  const codeTypes = useSelector(state => state.nref.codeTypes.map(e => ({ ...e, name: translate(`common:${e.name}`) })));
  const codeGroups = useSelector(state => state.nref.codeGroups);

  const equipmentStatuses = useSelector(state => state.dispatch.equipmentStatuses);
  const currentFilter = useSelector(state => state.dispatch.currentEquipmentStatusFilter);
  const closures = useSelector(state => state.manager.closures);
  const userAccessLevel = useSelector(state => state.auth.user.level);
  const { value: userLimitEditConfig } = usePlatformConfigurationUserAccessLevel(userAccessLevel);
  const { value: enableClosure } = usePlatformConfiguration('enable_closure');

  const filterCodeGroups = u => (currentFilter.equip_type === u.id_equip)
    && (selectedFields.code_type === -1 || u.code_type === selectedFields.code_type);

  const handleUpdateItems = () => {
    const selectedEquipmentStatuses = equipmentStatuses.filter(
      e => selectedItems.find(sel => sel.id === e.id),
    );

    const timestampToDate = timestamp => moment.unix(timestamp / 1000).format('DD/MM/YYYY');

    const newSelectedItems = selectedEquipmentStatuses.map((i) => {
      const startDate = (
        selectedFields.start_date
          ? timestampToDate(selectedFields.start_date)
          : i.start_date
      );

      const startTime = (
        selectedFields.start_time
          ? selectedFields.start_time
          : i.start_time
      );

      const startTimestamp = parseDateTimeToTimestamp(startDate, startTime) / 1000;

      return {
        ...i,
        start_date: startDate,
        start_time: startTime,
        start_timestamp: startTimestamp,
        code_type: selectedFields.code_type !== -1 ? selectedFields.code_type : i.code_type,
        id_code_group: selectedFields.id_code_group !== -1
          ? selectedFields.id_code_group : i.id_code_group,
        id_code: selectedFields.id_code ? selectedFields.id_code : i.id_code,
      };
    });

    return dispatch(updateEquipmentStatus({ equipmentStatus: newSelectedItems }));
  };

  const filterCodes = c => selectedFields.code_type === c.code_type
    && (selectedFields.id_code_group === 0 || c.id_group === selectedFields.id_code_group)
    && (currentFilter.equip_type === c.id_equip);


  const notRequiredFields = [
    'start_date',
    'start_time',
  ];

  // só pode salvar a alteração caso todos os campos sejam preenchidos.
  const disableSaveButton = Object.keys(selectedFields)
    .filter(k => (
      selectedFields[k] === INIT_SELECTED_FIELDS[k]
      && !notRequiredFields.includes(k)
    )).length;

  const modalHandleChange = (field, value) => {
    setSelectedFields((prevSelectedFields) => {
      switch (field) {
        case 'code_type':
          return {
            ...prevSelectedFields, id_code_group: 0, id_code: 0, code_type: value,
          };
        case 'id_code_group':
          return { ...prevSelectedFields, id_code: 0, id_code_group: value };
        default:
          return { ...prevSelectedFields, [field]: value };
      }
    });
  };

  const handleBack = () => {
    setIsConfirmChangesStep(false);
  };

  const handleContinue = () => {
    setIsConfirmChangesStep(true);
  };

  const handleSave = () => {
    handleUpdateItems();
    handleClose();
  };

  const handleDiscardChanges = () => {
    setSelectedFields(INIT_SELECTED_FIELDS);
    setFormikErrors({});
    if (formikRef.current) {
      formikRef.current.resetForm(INIT_SELECTED_FIELDS);
    }
  };

  const renderHeader = () => (
    <div>
      <Typography
        variant="h4"
        align="center"
        gutterBottom
      >
        {`${translate('common:EditEntries')}`}
      </Typography>
    </div>
  );

  const renderSelectField = ({
    fieldName,
    formLabel,
    collection,
  }) => {
    const defaultSelectValues = [
      {
        value: INIT_SELECTED_FIELDS[fieldName],
        label: translate('common:Select'),
      },
    ];

    const normalizedCollection = defaultSelectValues.concat(
      ...collection.map(e => ({ value: e.id, label: e.name })),
    );

    return (
      <FormControlDefaults formLabel={formLabel} classes={classes} isRequired>
        <Field
          name={fieldName}
          render={({ field, form }) => (
            <PF2MSearchSelectOutlined
              {...field}
              placeholder={null}
              className={classnames('form-control', classes.fieldModal)}
              onChange={(e) => {
                form.setFieldValue(fieldName, e.target.value);
                modalHandleChange(fieldName, e.target.value);
              }}
            >
              {normalizedCollection}
            </PF2MSearchSelectOutlined>
          )}
        />
      </FormControlDefaults>
    );
  };

  const renderDateField = ({ fieldName, formLabel }) => {
    let userLimitEdit = subtractDaysFromNow(userLimitEditConfig);
    const isEnableClosureOn = enableClosure && closures?.length > 0;

    if (isEnableClosureOn) {
      const dateNow = newMomentDate();
      const lastClosureDate = parseDate(closures[0].timestampClose, true);
      const currentDaysFromLastClosure = dateNow.diff(lastClosureDate, 'days');

      const lockEditConfig = subtractDaysFromNow(userLimitEditConfig);
      const lockEditLastClosure = subtractDaysFromNow(currentDaysFromLastClosure);

      userLimitEdit = currentDaysFromLastClosure > userLimitEditConfig
        ? lockEditConfig : lockEditLastClosure.add(1, 'day');
    }

    return (
      <FormControlDefaults formLabel={formLabel} classes={classes}>
        <Field
          name={fieldName}
          render={({ field, form }) => (
            <PF2MDatePickerOutlined
              field={field}
              form={form}
              minDate={userLimitEdit}
              onChange={(e) => {
                form.setFieldValue(fieldName, e, true);
                modalHandleChange(fieldName, e);
              }}
            />
          )}
        />
      </FormControlDefaults>
    );
  };

  const renderTimeField = ({
    fieldName,
    formLabel,
    values,
    errors,
  }) => (
    <FormControlDefaults formLabel={formLabel} classes={classes}>
      <Field
        name={fieldName}
        render={({ field, form }) => (
          <TimeCell
            {...field}
            error={errors[fieldName]}
            helperText={errors[fieldName]}
            value={values[fieldName]}
            bulkEdit={(e) => {
              form.setFieldValue(fieldName, e.target.value);
              modalHandleChange(fieldName, e.target.value);
            }}
          />
        )}
      />
    </FormControlDefaults>
  );

  const renderFormControl = () => (
    <Formik
      ref={formikRef}
      initialValues={selectedFields}
      validateOnChange
      validate={({ start_time: st, start_date: sd }) => {
        const errors = {};
        const startDate = typeof sd === 'number' ? moment.unix(sd) : sd;

        const currentMoment = newMomentDate();
        const dateTimeString = `${startDate.format('DD/MM/YYYY')} ${st}`;
        const dateTimeMoment = moment(dateTimeString, 'DD/MM/YYYY HH:mm:ss', true);

        if (!verifyTime(st)) {
          errors.start_time = translate('validation:InvalidHour');
        } else if (dateTimeMoment.isAfter(currentMoment)) {
          errors.start_time = translate('validation:FutureTimeCantBeSelected');
        }

        if (!startDate.isValid()) {
          errors.start_date = translate('validation:InvalidDate');
        }

        if (dateTimeMoment.startOf('day').isAfter(currentMoment.startOf('day'))) {
          errors.start_date = translate('validation:FutureDateCantBeSelected');
        }

        setFormikErrors(errors);
        return errors;
      }}
    >
      {({ values, errors }) => (
        <Form
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridColumnGap: 25,
            gridRowGap: 10,
          }}
        >
          {renderDateField({
            fieldName: 'start_date',
            formLabel: translate('common:Date'),
          })}
          {renderTimeField({
            values,
            errors,
            fieldName: 'start_time',
            formLabel: translate('commom:Time'),
          })}
          <br />
          {renderSelectField({
            fieldName: 'code_type',
            formLabel: translate('common:CodeType'),
            collection: codeTypes,
          })}
          {renderSelectField({
            fieldName: 'id_code_group',
            formLabel: translate('common:CodeGroup'),
            collection: codeGroups.filter(filterCodeGroups),
          })}
          {renderSelectField({
            fieldName: 'id_code',
            formLabel: translate('common:Code'),
            collection: codes.filter(filterCodes),
          })}
        </Form>
      )}
    </Formik>
  );

  const selectedCode = codes.find(c => (
    c.id === selectedFields.id_code
    && c.id_equip === currentFilter.equip_type
  ));

  const isInvisibleCode = [0, 3].includes(selectedCode?.is_visible)
  || [3, 4].includes(selectedCode?.code_type);

  const renderConfirmChanges = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 32,
      }}
    >
      {isInvisibleCode ? (
        <>
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 20,
            }}
          >
            <WarningOutlined style={{ fontSize: 46, color: '#FFC107' }} />
            <Typography style={{
              fontWeight: 'bold',
              textTransform: 'uppercase',
              fontSize: 30,
            }}
            >
              {translate('common:AttentionAlert')}
            </Typography>
          </div>
          <div
            style={{
              textAlign: 'start',
              gap: 20,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography style={{ fontSize: 24 }}>
              {translate('common:InvisibleCodeAlert')}
            </Typography>
            <Typography style={{ fontSize: 24, fontWeight: 'bold' }}>
              {translate('common:OnlyVisibleOnPlatform')}
            </Typography>
            <Typography style={{ fontSize: 24 }}>
              {translate('common:ToSendTurnOnVisible')}
            </Typography>
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              justifyContent: 'center',
              marginTop: 20,
            }}
          >
            <WarningOutlined style={{ fontSize: 46, color: '#FFC107' }} />
            <Typography style={{
              fontWeight: 'bold',
              textTransform: 'uppercase',
              fontSize: 30,
            }}
            >
              {translate('common:AttentionAlert')}
            </Typography>
          </div>
          <div style={{
            textAlign: 'start', gap: 20, display: 'flex', flexDirection: 'column',
          }}
          >
            <Typography style={{ fontSize: 24 }}>
              {translate('common:ChangeWillBeSent')}
            </Typography>
            <Typography style={{ fontSize: 24, fontWeight: 'bold' }}>
              {translate('common:OperatorWillReceiveMessageAlert')}
            </Typography>
            <Typography style={{ fontSize: 24 }}>
              {translate('common:DoYouWantToContinue')}
            </Typography>
          </div>
        </>
      )}
    </div>
  );

  const renderConfirmButtons = () => (
    <div className={classes.wrapperButtons}>
      <PF2MGenericButton
        color="primary"
        onClick={handleBack}
        startIcon={<ArrowBackOutlined fontSize="small" />}
      >
        {translate('common:Back')}
      </PF2MGenericButton>
      <PF2MGenericButton
        variant="contained"
        color="secondary"
        onClick={handleSave}
        disabled={
          Boolean(disableSaveButton)
          || Boolean(Object.keys(formikErrors).length)
        }
        startIcon={<CheckCircleOutlineOutlined fontSize="small" />}
      >
        {translate('common:Commit Changes')}
      </PF2MGenericButton>
    </div>
  );

  const renderFormButtons = () => (
    <div className={classes.wrapperButtons}>
      <PF2MGenericButton
        color="primary"
        onClick={handleDiscardChanges}
        startIcon={<DeleteOutline fontSize="small" />}
      >
        {translate('common:DiscardChanges')}
      </PF2MGenericButton>
      <PF2MGenericButton
        variant="contained"
        color="secondary"
        onClick={handleContinue}
        disabled={
          Boolean(disableSaveButton)
          || Boolean(Object.keys(formikErrors).length)
        }
        startIcon={<ArrowForwardOutlined fontSize="small" />}
      >
        {translate('common:Continue')}
      </PF2MGenericButton>
    </div>
  );

  return (
    <div>
      <Card style={{ maxWidth: 770 }}>
        <CardHeader icon>
          <CardIcon color="warning">
            <CreateIcon />
          </CardIcon>
        </CardHeader>
        <CardBody style={{ padding: '0 50px 50px 50px' }}>
          {renderHeader()}
          <hr />
          {isConfirmChangesStep ? renderConfirmChanges() : renderFormControl()}
          <div
            style={{
              display: 'flex',
              marginTop: 20,
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {isConfirmChangesStep ? renderConfirmButtons() : renderFormButtons()}
          </div>
        </CardBody>
      </Card>
      <PF2MAlertDialog
        hasCancel={false}
        confirmText={translate('common:Understood')}
        description={translate('validation:ThereAreBlankFields')}
        open={isModalOpen}
        onConfirm={() => setModalOpen(false)}
      />
    </div>
  );
};

EquipmentStatusEditAllModal.propTypes = {
  classes: PropTypes.object.isRequired,
  selectedItems: PropTypes.array,
  handleClose: PropTypes.func.isRequired,
};

EquipmentStatusEditAllModal.defaultProps = {
  selectedItems: [],
};

export default withStyles(styles)(EquipmentStatusEditAllModal);
